import { useAuth } from "@dl/web-core-js/auth";
import { Card, Field, MultiSelect } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { WarehouseClient, type Market, type ProductDTO } from "../../api/product";

/**
 * Which warehouses may dispatch this product, per market.
 *
 * An empty selection means the product is NOT SOLD in that market: its
 * dispatchable inventory there is zero, whatever any inventory rule says, and
 * the feeds, the pipeline and the transfer plan all read it that way. There
 * is no default warehouse and never a fallback — the list is the only
 * statement of where a product can be sold. The hint and the placeholder say
 * exactly that, because the previous wording ("the market's default
 * warehouse") was the opposite of what the server does.
 *
 * Bundles are read-only here regardless of role: their dispatch is derived
 * from the component items, and an override on the bundle would be ignored.
 */
export function DispatchWarehousesTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new WarehouseClient(http), [http]);

  const { data: warehouses } = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => client.findAll(),
    staleTime: 30 * 60_000,
  });

  const locked = readOnly || draft.type === "BUNDLE";

  const options = (warehouses ?? [])
    .filter((warehouse) => warehouse.is_active && warehouse.kind === "DISTRIBUTION")
    .map((warehouse) => ({
      value: String(warehouse.id),
      label: `${warehouse.code} · ${warehouse.name ?? ""}`.trim(),
    }));

  const selection = (market: Market): string[] =>
    (draft.source_warehouses?.[market] ?? []).map(String);

  const setMarket = (market: Market, ids: string[]) =>
    onChange({
      ...draft,
      source_warehouses: { ...(draft.source_warehouses ?? {}), [market]: ids.map(Number) },
    });

  const markets: Array<{ market: Market; label: string }> = [
    { market: "CA", label: t("product.dispatch.ca", "Canada") },
    { market: "US", label: t("product.dispatch.us", "United States") },
  ];

  return (
    <div className="grid max-w-4xl gap-4 lg:grid-cols-2">
      {markets.map(({ market, label }) => (
        <Card key={market} className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{label}</h3>
          <Field
            label={t("product.dispatch.warehouses", "Dispatch warehouses")}
            htmlFor={`dispatch-${market}`}
            hint={
              draft.type === "BUNDLE"
                ? t("product.dispatch.bundle", "Derived from the bundle's items")
                : t("product.dispatch.hint", "Empty means not sold in this market")
            }
          >
            <MultiSelect
              id={`dispatch-${market}`}
              disabled={locked}
              options={options}
              value={selection(market)}
              onChange={(ids: string[]) => setMarket(market, ids)}
              placeholder={t("product.dispatch.none", "Not sold in this market")}
            />
          </Field>
        </Card>
      ))}
    </div>
  );
}
